// web/src/screens/Home.tsx — Home by site stage (portal spec 14): checklist → going live → this month.
// sold/finishing → "We need three things from you"; going_live → "We're putting your site live"; live/legacy → Ask for a
// change + Send photos + this month's suggestions (spec 18) + the latest report (spec 17); paused/offboarding → banners.
// One block per site.
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { HomeSite } from '../lib/types';
import { PageHeader } from '../components/PageHeader';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { Skeleton } from '../components/Skeleton';
import { GoLiveChecklist } from '../components/GoLiveChecklist';
import { NeedsActionItem } from '../components/NeedsActionItem';
import { RequestCard } from '../components/RequestCard';
import { StageBanner } from '../components/StageBanner';
import { ReportCard } from '../components/ReportCard';
import { SuggestionsCard } from '../components/SuggestionsCard';
import { Icon } from '../components/Icon';

export function Home() {
  const q = useQuery({ queryKey: ['home'], queryFn: () => api.home() });
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  useEffect(() => { // back from Stripe Checkout: force a fresh plan check, then clean the URL
    if (params.get('plan') !== 'started') return;
    qc.invalidateQueries({ queryKey: ['onboarding'] });
    setParams({}, { replace: true });
  }, [params, qc, setParams]);
  return (
    <>
      <PageHeader title="Home" subtitle="Your website, in one place." />
      <AsyncBoundary query={q} skeleton={<Skeleton variant="card" count={3} />}>
        {(feed) => (
          <div className="flex flex-col gap-7">
            {feed.sites.length === 0 && (
              <p className="rounded-lg border border-hair bg-surface/40 px-4 py-6 text-center text-sm text-muted">Your site will appear here as soon as your account is set up.</p>
            )}
            {feed.sites.map((s: HomeSite) => (
              <section key={s.id} className="flex min-w-0 flex-col gap-3">
                {feed.sites.length > 1 && <h2 className="font-head text-sm text-muted">{s.name}</h2>}
                {(s.stage === 'sold' || s.stage === 'finishing') && <GoLiveChecklist site={s} />}
                {s.stage === 'going_live' && (
                  <div className="rounded-lg border border-hair border-l-4 border-l-run bg-surface p-5">
                    <h2 className="flex items-center gap-2 font-head text-ink"><Icon name="rocket" size={18} className="text-run" />We’re putting your site live</h2>
                    <p className="mt-1 text-sm text-muted">Nothing for you to do. We’ll email you when it’s up.</p>
                  </div>
                )}
                {(s.stage === 'paused' || s.stage === 'offboarding') && <StageBanner site={s} />}
                {(s.stage === 'live' || s.stage === 'legacy') && (
                  <>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => nav(`/request?site=${s.id}`)} className="inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 font-medium text-runink"><Icon name="plus" size={16} />Ask for a change</button>
                      <button onClick={() => nav(`/photo?site=${s.id}`)} className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-ink"><Icon name="image" size={16} />Send photos</button>
                    </div>
                    <SuggestionsCard siteId={s.id} />
                    <ReportCard siteId={s.id} />
                  </>
                )}
              </section>
            ))}
            {feed.needsAction.length > 0 && (
              <section>
                <h2 className="mb-3 font-head text-sm text-muted">Needs your answer</h2>
                <div className="flex flex-col gap-2">
                  {feed.needsAction.map((a) => (
                    <NeedsActionItem key={a.kind === 'suggestions' ? `s-${a.proposalId}` : a.run.id} item={a}
                      siteName={a.kind === 'suggestions' && feed.sites.length > 1 ? feed.sites.find((s) => s.id === a.siteId)?.name : undefined} />
                  ))}
                </div>
              </section>
            )}
            {feed.inFlight.length > 0 && (
              <section>
                <h2 className="mb-3 font-head text-sm text-muted">In progress</h2>
                <div className="flex flex-col gap-3">{feed.inFlight.map((r) => <RequestCard key={r.id} run={r} />)}</div>
              </section>
            )}
          </div>
        )}
      </AsyncBoundary>
    </>
  );
}
